import { useState } from 'react';
import { ApiError, createLeague, createPreset, putMgmtLeague, putRuleset, setActiveLeagueId } from '../../core/api';
import type { MediaType, Pace } from '../../core/types';
import { SCORING_PRESETS as PRESETS, scoringLine } from '../scoringPresets';
import { inviteMessage } from '../format';
import s from './CreateLeagueWizard.module.css';

// Create a league in the right order: name + mode → configure → CREATE (which is
// when the league and its invite code first exist). Config comes before the code so
// no one can join — or, in Jukebox mode, propose rounds — before the rules are set.
// The final button runs, in order: createLeague(name) → PUT settings (mode, pace,
// chat, upcoming, max) → PUT ruleset (chosen scoring preset + veto) → show the code.

type Mode = 'dj' | 'jukebox';

const PACES: { key: Pace; label: string; note: string }[] = [
  { key: 'rapid', label: 'Rapid', note: '24h a phase' },
  { key: 'steady', label: 'Steady', note: '3 days' },
  { key: 'slow', label: 'Slow', note: '1 week' },
];
const MEDIA: { key: MediaType; label: string; note: string }[] = [
  { key: 'audio', label: 'Music', note: 'Spotify' },
  { key: 'video', label: 'Video', note: 'YouTube' },
  { key: 'image', label: 'Images', note: 'Upload' },
];

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return <button type="button" className={`${s.sw}${on ? ' ' + s.swon : ''}`} onClick={() => onChange(!on)} aria-pressed={on} aria-label="toggle" />;
}

export function CreateLeagueWizard({ onCancel, onCreated }: { onCancel: () => void; onCreated: (leagueId: number) => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<Mode>('dj');
  const [chat, setChat] = useState(true);
  const [future, setFuture] = useState(true);
  const [max, setMax] = useState('');
  const [preset, setPreset] = useState('standard'); // DJ: single scoring
  const [presetKeys, setPresetKeys] = useState<string[]>(['standard', 'relaxed']); // Jukebox: which to seed
  const [veto, setVeto] = useState(true); // DJ only (Jukebox veto is per-preset)
  const [paces, setPaces] = useState<Pace[]>(['rapid', 'steady', 'slow']);
  const [media, setMedia] = useState<MediaType[]>(['audio', 'video', 'image']);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [made, setMade] = useState<{ id: number; code: string } | null>(null);

  const chosen = PRESETS.find((p) => p.key === preset) ?? PRESETS[0];
  // DJ: picking a preset seeds the veto toggle from its default; the toggle can override.
  function pickPreset(key: string) {
    setPreset(key);
    setVeto(PRESETS.find((p) => p.key === key)?.veto ?? true);
  }
  // Jukebox: multi-select which starter scorings to create as named presets (≥1).
  function togglePresetKey(key: string) {
    setPresetKeys((cur) => (cur.includes(key) ? (cur.length > 1 ? cur.filter((x) => x !== key) : cur) : [...cur, key]));
  }
  function togglePace(p: Pace) {
    setPaces((cur) => (cur.includes(p) ? (cur.length > 1 ? cur.filter((x) => x !== p) : cur) : [...cur, p]));
  }
  function toggleMedia(m: MediaType) {
    setMedia((cur) => (cur.includes(m) ? (cur.length > 1 ? cur.filter((x) => x !== m) : cur) : [...cur, m]));
  }

  async function create() {
    if (busy) return;
    setBusy(true); setErr(null);

    // Step 1 — the ONLY step that may fail back to a retry. If this throws, no league
    // was created, so retrying is safe.
    let league: Awaited<ReturnType<typeof createLeague>>;
    try {
      league = await createLeague(name.trim(), mode); // pass mode up front so the season seeds correctly (Jukebox → proposals open + cap)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create the league — try again.');
      setBusy(false);
      return;
    }

    // From here the league EXISTS. Everything below is best-effort — a failure here must
    // NEVER show "failed" (that caused people to retry and end up with duplicate
    // leagues). Anything unsaved can be fixed in League settings afterwards.
    setActiveLeagueId(league.league_id);
    try {
      await putMgmtLeague({
        league_type: mode,
        chat_enabled: chat,
        future_rounds_visible: future,
        max_participants: max.trim() ? Number(max) : null,
        allowed_paces: paces,
        allowed_media: media,
      });
      if (mode === 'jukebox') {
        // Seed the chosen starter scorings as NAMED presets so members can propose
        // immediately. Per-preset best-effort: one that already exists is skipped, not
        // fatal (the backend seeds a base ruleset on create, so a name can collide).
        const picked = PRESETS.filter((p) => presetKeys.includes(p.key));
        for (const p of picked) {
          try { await createPreset({ name: p.name, ...p.nums, veto_enabled: p.veto }); }
          catch { /* already exists / non-fatal — owner can manage in Admin → Scoring presets */ }
        }
      } else {
        try { await putRuleset({ ...chosen.nums, veto_enabled: veto }); } catch { /* non-fatal */ }
      }
    } catch { /* settings partially applied — the league still exists and is usable */ }

    setMade({ id: league.league_id, code: league.join_code });
    setStep(3);
    setBusy(false);
  }

  return (
    <div className={s.wrap}>
      <div className={s.card}>
        {step !== 3 ? (
          <div className={s.steps}>
            <span className={step >= 1 ? s.on : ''}>1 · Name &amp; mode</span>
            <span className={s.sep} />
            <span className={step >= 2 ? s.on : ''}>2 · Set up</span>
            <span className={s.sep} />
            <span>3 · Invite</span>
          </div>
        ) : null}

        {step === 1 ? (
          <>
            <h2>Start a league</h2>
            <label className={s.lbl}>League name</label>
            <input className={s.input} placeholder="e.g. Sunday Roasters" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
            <label className={s.lbl}>How are rounds made?</label>
            <div className={s.modes}>
              <button type="button" className={`${s.mode}${mode === 'dj' ? ' ' + s.modeon : ''}`} onClick={() => setMode('dj')}>
                <b>DJ mode</b><small>You build every round yourself.</small>
              </button>
              <button type="button" className={`${s.mode}${mode === 'jukebox' ? ' ' + s.modeon : ''}`} onClick={() => setMode('jukebox')}>
                <b>Jukebox mode</b><small>Members propose rounds; they’re drawn at random.</small>
              </button>
            </div>
            <div className={s.foot}>
              <button className={s.link} onClick={onCancel}>Cancel</button>
              <button className={s.primary} disabled={!name.trim()} onClick={() => setStep(2)}>Next →</button>
            </div>
          </>
        ) : step === 2 ? (
          <>
            <h2>Set up {name.trim()}</h2>
            <p className={s.sub}>Lock these in before you invite anyone — you can change them later in League settings.</p>

            <label className={s.lbl}>Scoring{mode === 'jukebox' ? ' — presets members pick from' : ''}</label>
            <div className={s.modes}>
              {PRESETS.map((p) => {
                const on = mode === 'jukebox' ? presetKeys.includes(p.key) : preset === p.key;
                return (
                  <button type="button" key={p.key} className={`${s.mode}${on ? ' ' + s.modeon : ''}`} onClick={() => (mode === 'jukebox' ? togglePresetKey(p.key) : pickPreset(p.key))}>
                    <b>{p.name}{mode === 'jukebox' && p.veto ? '' : ''}</b><small>{scoringLine(p.nums)}{mode === 'jukebox' ? (p.veto ? ' · veto on' : ' · no veto') : ''}</small>
                  </button>
                );
              })}
            </div>
            {mode === 'jukebox' ? <p className={s.sub}>Pick one or more — members choose from these when proposing (each keeps its own veto rule). Add or edit them anytime in Admin → Scoring presets.</p> : null}

            <label className={s.lbl}>Allowed media</label>
            <div className={s.paces}>
              {MEDIA.map((m) => {
                const on = media.includes(m.key);
                const last = on && media.length === 1;
                return (
                  <button type="button" key={m.key} className={`${s.mode}${on ? ' ' + s.modeon : ''}`} disabled={last} title={last ? 'Keep at least one' : undefined} onClick={() => toggleMedia(m.key)}>
                    <b>{m.label}</b><small>{m.note}</small>
                  </button>
                );
              })}
            </div>
            <p className={s.sub}>Which kinds of entries {mode === 'jukebox' ? 'members can propose' : 'rounds can use'}.</p>

            <label className={s.lbl}>Round pace</label>
            <div className={s.paces}>
              {PACES.map((p) => {
                const on = paces.includes(p.key);
                const last = on && paces.length === 1;
                return (
                  <button type="button" key={p.key} className={`${s.mode}${on ? ' ' + s.modeon : ''}`} disabled={last} title={last ? 'Keep at least one pace' : undefined} onClick={() => togglePace(p.key)}>
                    <b>{p.label}</b><small>{p.note}</small>
                  </button>
                );
              })}
            </div>
            <p className={s.sub}>Pick one for a fixed pace, or several to let it vary{mode === 'jukebox' ? ' — members choose from these when proposing' : ''}.</p>

            {mode === 'dj' ? (
              <div className={s.opt}>
                <span className={s.ol}><b>Veto</b><small>Let players veto a track — it’s knocked out when {chosen.nums.veto_threshold_pct}% of players veto it.</small></span>
                <Toggle on={veto} onChange={setVeto} />
              </div>
            ) : null}
            <div className={s.opt}>
              <span className={s.ol}><b>Chat</b><small>Let players talk in the league chat.</small></span>
              <Toggle on={chat} onChange={setChat} />
            </div>
            <div className={s.opt}>
              <span className={s.ol}><b>Show upcoming rounds</b><small>Reveal scheduled rounds before they open.</small></span>
              <Toggle on={future} onChange={setFuture} />
            </div>
            <div className={s.opt}>
              <span className={s.ol}><b>Max players</b><small>Leave blank for no limit.</small></span>
              <input className={s.num} type="number" min={2} inputMode="numeric" placeholder="No limit" value={max} onChange={(e) => setMax(e.target.value)} />
            </div>
            {err ? <p className={s.err}>{err}</p> : null}
            <div className={s.foot}>
              <button className={s.link} onClick={() => setStep(1)}>← Back</button>
              <button className={s.primary} disabled={busy} onClick={create}>{busy ? 'Creating…' : 'Create league'}</button>
            </div>
          </>
        ) : made ? (
          <>
            <div className={s.tick}>✓</div>
            <h2>{name.trim()} is ready</h2>
            <p className={s.sub}>Share this code so your mates can join. A round needs at least 2 players, so invite someone before you start one.</p>
            <div className={s.codebox}>
              <div><small>Invite code</small><b>{made.code}</b></div>
              <button className={s.ghost} onClick={() => navigator.clipboard?.writeText(inviteMessage(made.code))}>Copy invite</button>
            </div>
            <div className={s.foot}>
              <button className={s.link} onClick={onCancel}>Back to leagues</button>
              <button className={s.primary} onClick={() => onCreated(made.id)}>Go to your league →</button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
